import React, { ComponentProps } from 'react';
import {
  Autocomplete,
  ListItem,
  ListItemText,
  outlinedInputClasses,
  TextField,
  Typography,
} from '@rossum/ui/material';
import {
  GridRenderEditCellParams,
  useGridApiContext,
} from '@rossum/ui/x-data-grid-pro';
import { GridRowModel } from '../../rows/rowTypes';
import { OptionWithHelpText } from '../types';
import { useHandleSelectionSync } from './useHandleSelectionSync';

export type EditCellAutocompleteProps<
  T,
  R extends GridRowModel = GridRowModel,
> = GridRenderEditCellParams<R> & {
  AutocompleteProps?: Partial<
    ComponentProps<typeof Autocomplete<T, boolean | undefined, true, true>>
  >;
};

export type EditCellAutocompleteExtraProps = {
  setBatchEditError: React.Dispatch<React.SetStateAction<boolean>>;
};

export const EditCellAutocomplete = <T, R extends GridRowModel = GridRowModel>(
  props: EditCellAutocompleteProps<T, R> & EditCellAutocompleteExtraProps
) => {
  const {
    id,
    value,
    field,
    setBatchEditError,
    AutocompleteProps: { multiple, options, ...rest } = {},
  } = props;
  const apiRef = useGridApiContext();

  const valueWithFallback = value ?? (multiple ? [] : '');

  useHandleSelectionSync({
    apiRef: apiRef.current,
    id,
    hasError: false,
    field,
    setBatchEditError,
  });

  return (
    <Autocomplete
      multiple={multiple}
      freeSolo
      onChange={(e, newValue) => {
        if (multiple) {
          e.stopPropagation();
        }

        apiRef.current.setEditCellValue({ id, field, value: newValue });
      }}
      sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
      disableClearable
      value={valueWithFallback}
      options={options ?? []}
      renderInput={params => (
        <TextField
          {...params}
          autoFocus
          fullWidth
          InputProps={{
            ...params.InputProps,
            sx: {
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                border: 'unset !important',
              },
            },
          }}
        />
      )}
      {...rest}
    />
  );
};

type RenderOption = Required<
  ComponentProps<
    typeof Autocomplete<
      OptionWithHelpText | string,
      boolean | undefined,
      true,
      true
    >
  >
>['renderOption'];

export const renderOptionWithHelpText: RenderOption = (props, option) =>
  typeof option === 'string' ? (
    <ListItem {...props} dense>
      <ListItemText>{option}</ListItemText>
    </ListItem>
  ) : (
    <ListItem
      {...props}
      dense
      secondaryAction={
        <Typography variant="caption" sx={{ opacity: 0.5 }}>
          {option.helpText}
        </Typography>
      }
    >
      <ListItemText>{option.label}</ListItemText>
    </ListItem>
  );

export const renderMultipleEditAutocomplete =
  <T,>({ setBatchEditError }: EditCellAutocompleteExtraProps) =>
  (params: EditCellAutocompleteProps<T>) => (
    <EditCellAutocomplete
      {...params}
      AutocompleteProps={{ multiple: true }}
      setBatchEditError={setBatchEditError}
    />
  );
